import React from "react";
import { Star } from "lucide-react";
import DashboardPanel from "./DashboardPanel";
import { formatRate, getStatusColors } from "../../pages/performance/performanceUtils";

const fmtN = (n) => Number(n || 0).toLocaleString();

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-app-secondary px-3 py-2.5">
      <p className="text-[11px] font-medium uppercase tracking-wider text-app-muted">{label}</p>
      <p className="mt-0.5 text-lg font-bold tabular-nums text-app-primary">{value}</p>
    </div>
  );
}

// My conversion against the team's, on one shared scale.
function ConversionCompare({ mine, team }) {
  const max = Math.max(mine, team, 1);
  const rows = [
    { label: "You", value: mine, bar: "bg-blue-500 dark:bg-sky-400" },
    { label: "Team", value: team, bar: "bg-gray-400 dark:bg-slate-500" },
  ];
  return (
    <div>
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-app-muted">
        Conversion rate vs team
      </h4>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center gap-3 text-xs">
            <span className="w-10 shrink-0 font-medium text-app-secondary">{r.label}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700">
              <div className={`h-full rounded-full ${r.bar}`} style={{ width: `${(r.value / max) * 100}%` }} />
            </div>
            <span className="w-12 shrink-0 text-right font-semibold tabular-nums text-app-primary">
              {formatRate(r.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The signed-in user's leaderboard row: status, rank, output/effort and conversion. */
function MyPerformanceCard({ data, loading = false, error = false }) {
  let body;
  if (loading) {
    body = (
      <div className="space-y-4" aria-hidden="true">
        <div className="h-6 w-32 animate-pulse rounded-full bg-gray-200 dark:bg-slate-700" />
        <div className="grid grid-cols-2 gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-gray-100 dark:bg-slate-700/60" />
          ))}
        </div>
        <div className="h-10 animate-pulse rounded bg-gray-100 dark:bg-slate-700/60" />
      </div>
    );
  } else if (error) {
    body = <p className="text-sm text-app-error">{"Couldn't load performance data."}</p>;
  } else if (!data?.me) {
    body = (
      <p className="text-sm text-app-muted">
        No performance data for you yet. It appears once you log activity on your projects.
      </p>
    );
  } else {
    const { me, rankedCount, teamConversion } = data;
    const colors = getStatusColors(me.status);
    body = (
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${colors.bg} ${colors.text} ${colors.border}`}
          >
            {me.status === "Top Performer" ? (
              <Star size={12} fill="currentColor" strokeWidth={0} />
            ) : (
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colors.dot }} />
            )}
            {me.status || "Unrated"}
          </span>
          <span className="text-sm text-app-muted">
            Rank <span className="font-semibold text-app-primary">#{me.rank}</span> of {rankedCount}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Screener responses" value={fmtN(me.totalOutput)} />
          <Stat label="Effort" value={fmtN(me.totalEffort)} />
        </div>
        <ConversionCompare mine={Number(me.conversionRate) || 0} team={teamConversion} />
        <p className="text-[11px] text-app-muted">
          Screener responses are output; every other logged action is effort. Conversion = output ÷ effort.
        </p>
      </div>
    );
  }

  return (
    <DashboardPanel
      title="My Performance"
      description="Across all projects."
      action={{ label: "Leaderboard", to: "/admin/performance-dashboard" }}
    >
      {body}
    </DashboardPanel>
  );
}

export default MyPerformanceCard;
